import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';

export type AdminUser = {
	userId: string;
	email: string;
	enabled: boolean;
	creationDate: string;
	lastModifiedDate: string;
};

export type AdminUsersPage = {
	users: AdminUser[];
	nextToken?: string;
};

export type AdminUserGroup = {
	groupName: string;
	description?: string;
	creationDate?: string;
};

export type AdminUserOperationResult = {
	success: boolean;
	errorMessage?: string;
};

@Injectable({ providedIn: 'root' })
export class AdminUsersService {
	private readonly http = inject(HttpClient);

	getUsers(limit?: number, nextToken?: string, email?: string): Observable<AdminUsersPage> {
		let params = new HttpParams();
		if (limit != null) params = params.set('limit', limit);
		if (nextToken) params = params.set('nextToken', nextToken);
		if (email) params = params.set('email', email);
		return this.http.get<AdminUsersPage>(`${environment.api}/admin/users`, { params });
	}

	deleteUser(userId: string): Observable<AdminUserOperationResult> {
		return this.http
			.delete<void>(`${environment.api}/admin/users/${userId}`, { observe: 'response' })
			.pipe(
				map(r => ({ success: r.status === 200 || r.status === 204 })),
				catchError(err => of({ success: false, errorMessage: err.error?.message ?? err.message }))
			);
	}

	getUserGroups(userId: string): Observable<AdminUserGroup[]> {
		return this.http
			.get<{ groups: AdminUserGroup[] }>(`${environment.api}/admin/users/${userId}/groups`)
			.pipe(map(r => r.groups));
	}

	addUserToGroup(userId: string, groupName: string): Observable<AdminUserOperationResult> {
		return this.http
			.post<void>(`${environment.api}/admin/users/${userId}/groups`, { groupName }, { observe: 'response' })
			.pipe(
				map(r => ({ success: r.status === 200 || r.status === 201 })),
				catchError(err => of({ success: false, errorMessage: err.error?.message ?? err.message }))
			);
	}

	removeUserFromGroup(userId: string, groupName: string): Observable<AdminUserOperationResult> {
		return this.http
			.delete<void>(`${environment.api}/admin/users/${userId}/groups/${groupName}`, { observe: 'response' })
			.pipe(
				map(r => ({ success: r.status === 200 || r.status === 204 })),
				catchError(err => of({ success: false, errorMessage: err.error?.message ?? err.message }))
			);
	}
}
